import { Box, ButtonProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import palette from '@ui/palette';
import Icon, { IconProps } from '@ui/Icon';
import TextButton from './TextButton';
import { paletteBridge } from '../../../../theme/palette';

type IconTextButtonVariants = 'uppercase' | 'capitalize' | 'none';
type IconTextButtonColor = 'primary' | 'secondary' | 'inherit';

export interface IconTextButtonProps extends Omit<ButtonProps, 'variant'> {
  iconType: IconProps['type'];
  buttonType?: IconTextButtonVariants;
  includeBackgroundColor?: boolean;
  color?: IconTextButtonColor;
  includeIconAsPrefix?: boolean;
  bold?: boolean;
}

const useStyles = makeStyles()(theme => ({
  root: {
    '&.Mui-disabled, &.Mui-disabled:hover': {
      opacity: 0.5,
    },
    gap: theme.spacing(0.5),
  },
  'color-primary-root': {
    '&.Mui-disabled, &.Mui-disabled:hover': {
      color: theme.palette.primary.main,
    },
  },
  'color-secondary-root': {
    '&.Mui-disabled, &.Mui-disabled:hover': {
      color: palette.gray_50,
    },
  },
  container: {
    padding: theme.spacing(0.375, 1),
    textAlign: 'center',
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: 'none',
    },
  },
  'color-primary-container': {
    '&:hover': {
      color: palette.mint_200,
    },
  },
  'color-secondary-container': {
    color: theme.palette.grey[400],
    '&:hover': {
      color: theme.palette.common.white,
    },
  },
  'color-secondary-container-include-background': {
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: paletteBridge.buttons.secondary.hover,
    },
  },
  icon: {
    height: theme.spacing(2),
    width: theme.spacing(2),
  },
}));

const IconTextButton = React.forwardRef<HTMLButtonElement, IconTextButtonProps>(
  (props, ref) => {
    const {
      children,
      iconType,
      buttonType = 'capitalize',
      className = '',
      color = 'secondary',
      includeBackgroundColor = true,
      includeIconAsPrefix = true,
      style = {},
      bold,
      ...rest
    } = props;

    const { classes, cx } = useStyles();

    const buttonTypeStyles = {
      textTransform: buttonType,
      fontWeight: bold || buttonType === 'uppercase' ? 'bold' : 'normal',
      ...style,
    };

    const rootClass = `color-${color}-root` as keyof typeof classes;
    const containerClass = `color-${color}-container` as keyof typeof classes;
    const containerWithBackgroundClass =
      `color-${color}-container-include-background` as keyof typeof classes;
    const textButtonClass = cx(
      className,
      classes.container,
      typeof color === 'string' && classes[containerClass],
      includeBackgroundColor &&
        color === 'secondary' &&
        classes[containerWithBackgroundClass]
    );

    return (
      <TextButton
        {...rest}
        ref={ref}
        color={color}
        className={textButtonClass}
        classes={{
          root: cx(classes.root, classes[rootClass]),
        }}
        style={buttonTypeStyles}
      >
        {includeIconAsPrefix && (
          <Box height={16} width={16}>
            <Icon type={iconType} className={classes.icon} />
          </Box>
        )}
        {children}
        {!includeIconAsPrefix && (
          <Box height={16} width={16}>
            <Icon type={iconType} className={classes.icon} />
          </Box>
        )}
      </TextButton>
    );
  }
);

export default IconTextButton;
